import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { QRCodeSVG } from 'qrcode.react';
import { AiBudgetApi, PlanCatalogue } from '@agendor/shared';
import {
    Button,
    Input,
    ConfirmDialog,
    Dialog,
    DialogHeader,
    DialogTitle,
    DialogContent,
    DialogFooter,
    GhostButton,
    gtagEvent,
    formatPhone,
    stripCountryCode,
    onlyDigits,
    vars,
    Badge,
} from '@agendor/ui';
import { FormGroup, InputGroup, Checkbox, CheckboxLabel, LabelRow, WarningBanner } from '../components/Input';
import { Modal } from '../components/Modal';
import BusinessService, { BusinessDetails, hasConnectorAccess, hasPaidAccess } from '../business-service';
import { useSingleModalRoute } from '../navigation';
import { useToast } from '../toast-context';
import MetaEmbeddedSignup, { MetaEmbeddedSignupError } from '../MetaEmbeddedSignup';
import PersonalizationCard from './PersonalizationCard';
import OpenScopeCard from './OpenScopeCard';
import TemplateMappingsCard from './TemplateMappingsCard';
import CreditBudgetCard from './CreditBudgetCard';
import {
    ConnectionChoice,
    ConnectionMethod,
    ConnectorConnectionStatus,
    ConnectorRiskNotice,
    ConnectorSetup,
    ConnectorTermsCheckbox,
} from './WhatsAppConnectorPanel';
import { isMobile } from '../platform';
import { connectorConnectionState, ConnectorConnectionState } from '../whatsapp-connector';
import { useVisibleInterval } from '../use-visible-interval';

// Upsell styles
const UpsellContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    max-width: 800px;
    margin: 0 auto;
    padding: 48px 24px;
    text-align: center;
`;

const IconWrapper = styled.div`
    width: 80px;
    height: 80px;
    border-radius: 20px;
    background: linear-gradient(135deg, #25d366 0%, #128c7e 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 32px;
`;

const WhatsAppIcon = () => (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="white">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
);

const UpsellTitle = styled.h1`
    font-size: 1.75rem;
    color: ${vars.foregroundColor};
    margin: 0 0 12px 0;
    font-weight: 700;
    line-height: 1.3;
`;

const UpsellSubtitle = styled.p`
    font-size: 1.1rem;
    color: #666;
    margin: 0 0 40px 0;
    line-height: 1.6;
`;

const FeaturesGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    width: 100%;
    margin-bottom: 40px;
    text-align: left;

    @media (max-width: 480px) {
        grid-template-columns: 1fr;
    }
`;

const FeatureCard = styled.div`
    background: #fff;
    border: 1px solid ${vars.dividerColor};
    border-radius: 12px;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const FeatureIcon = styled.span`
    font-size: 1.5rem;
    line-height: 1;
`;

const FeatureTitle = styled.span`
    font-weight: 600;
    color: ${vars.foregroundColor};
    font-size: 0.95rem;
`;

const FeatureDescription = styled.span`
    font-size: 0.85rem;
    color: #666;
    line-height: 1.4;
`;

const CTACard = styled.div`
    background: ${vars.primaryColorLight};
    border: 1px solid ${vars.dividerColor};
    border-radius: 12px;
    padding: 24px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    width: 100%;
    max-width: 320px;
`;

const PriceRow = styled.div`
    display: flex;
    align-items: baseline;
    gap: 4px;
`;

const PriceValue = styled.span`
    font-size: 1.5rem;
    font-weight: 700;
    color: ${vars.foregroundColor};
`;

const PriceUnit = styled.span`
    font-size: 0.9rem;
    color: ${vars.foregroundColor};
`;

const PriceNote = styled.span`
    font-size: 0.85rem;
    color: #666;
`;

// Config styles
const ConfigContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0;
    max-width: 800px;
    margin: 0 auto;
`;

const Header = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 24px;
`;

const SectionTitle = styled.h1`
    font-size: 1.5rem;
    color: ${vars.foregroundColor};
    margin: 0;
    font-weight: 600;
`;

export const ConfigCard = styled.div`
    background-color: #fff;
    border: 1px solid ${vars.dividerColor};
    border-radius: 8px;
    padding: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 24px;
    cursor: pointer;

    &:hover {
        border-color: ${vars.foregroundColor};
        background-color: ${vars.backgroundColorBright};
    }

    @media (max-width: 600px) {
        flex-direction: column;
        align-items: flex-start;
        gap: 16px;
    }
`;

export const ConfigInfo = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

export const ConfigTitle = styled.h3`
    margin: 0;
    font-size: 1.1rem;
    color: ${vars.foregroundColor};
    font-weight: 600;
`;

export const ConfigSummary = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    color: #666;
    font-size: 0.9rem;
`;

export const SummaryItem = styled.span`
    color: #666;
`;

// Test mode styles
const pulse = keyframes`
    0% { opacity: 1; }
    50% { opacity: 0.5; }
    100% { opacity: 1; }
`;

const PulseDot = styled.span`
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${vars.accentColor};
    animation: ${pulse} 2s ease-in-out infinite;
`;

const TestModeWarning = styled.div`
    padding: 16px;
    background-color: ${vars.warningTintSoft};
    border: 1px solid ${vars.dividerColor};
    border-radius: 8px;
    font-size: 0.9rem;
    color: ${vars.foregroundColor};
    line-height: 1.5;
`;

// Before Meta's window: the official connection only takes numbers on the WhatsApp Business app.
type ConnectPreludeStep = 'business-app-question' | 'move-to-business-app' | 'meta-window';

const PreludeBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    line-height: 1.5;

    a {
        color: ${vars.accentColor};
    }
`;

const PreludeSteps = styled.ol`
    margin: 0;
    padding-left: 20px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    opacity: 0.8;
`;

// Same shape as the connector's ConnectorRiskNotice, which sits in the same spot for that connection.
const MetaBillingNotice = styled.div`
    padding: 12px 16px;
    background-color: ${vars.warningTintSoft};
    border: 1px solid ${vars.dividerColor};
    border-radius: 8px;
    font-size: 0.9rem;
    line-height: 1.5;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
`;

const AutoMessagesHint = styled(WarningBanner)`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
`;

const TestModeQRContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    padding: 24px;
`;

const TestModeStatusText = styled.span`
    font-size: 0.9rem;
    color: #666;
`;

const TestModeConnectedRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const PairingCodeDisplay = styled.div`
    font-size: 1.5rem;
    font-weight: 700;
    letter-spacing: 0.15em;
    color: ${vars.foregroundColor};
    text-align: center;
    padding: 12px;
    background: ${vars.backgroundColorBright};
    border: 1px solid ${vars.dividerColor};
    border-radius: 8px;
`;

function TestModePairingSection({
    pairingCode,
    onRequestCode,
}: {
    pairingCode: string | null;
    onRequestCode: (phoneNumber: string) => void;
}) {
    const [phone, setPhone] = useState('');
    const { showToast } = useToast();

    if (pairingCode) {
        return (
            <div
                style={{
                    borderTop: `1px solid ${vars.dividerColor}`,
                    paddingTop: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                }}
            >
                <PairingCodeDisplay>
                    {pairingCode.slice(0, 4)} - {pairingCode.slice(4)}
                </PairingCodeDisplay>
                <Button
                    onClick={() => {
                        navigator.clipboard.writeText(pairingCode);
                        showToast('Código copiado!', 'success', 'modal');
                    }}
                >
                    Copiar código
                </Button>
                <TestModeStatusText>
                    No WhatsApp, vá em Aparelhos conectados → Conectar → &ldquo;Vincular com número de telefone&rdquo; e
                    digite o código acima.
                </TestModeStatusText>
            </div>
        );
    }

    return (
        <div
            style={{
                borderTop: `1px solid ${vars.dividerColor}`,
                paddingTop: '16px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
            }}
        >
            <TestModeStatusText>Digite o número que será conectado:</TestModeStatusText>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <Input
                    type="tel"
                    placeholder="(00) 00000-0000"
                    value={phone}
                    onChange={(e) => setPhone(formatPhone(e.target.value))}
                    maxLength={15}
                    style={{ flex: '1 1 200px', minWidth: '200px' }}
                />
                <Button
                    onClick={() => onRequestCode('55' + onlyDigits(phone))}
                    disabled={onlyDigits(phone).length < 11}
                >
                    Gerar código
                </Button>
            </div>
        </div>
    );
}

const TestModeLink = styled(GhostButton).attrs({ hoverBg: 'bright', hoverBorder: false })`
    font-size: 0.85rem;
`;

const features = [
    {
        icon: '🤖',
        title: 'Atendimento 24h',
        description: 'O assistente responde seus clientes a qualquer hora, no seu próprio número.',
    },
    {
        icon: '🎤',
        title: 'Entende áudio',
        description: 'Transcreve e responde mensagens de voz automaticamente.',
    },
    {
        icon: '📅',
        title: 'Agendamento na conversa',
        description: 'Clientes marcam, remarcam e cancelam direto no WhatsApp.',
    },
    {
        icon: '🔔',
        title: 'Lembretes automáticos',
        description: 'Confirmações e lembretes pelo WhatsApp para reduzir faltas.',
    },
];

type BotOverride = { phoneNumber: string; enabled: boolean };
type BotDraft = { enabled: boolean; overrides: BotOverride[] };

const connectorSummaryLabels: Record<ConnectorConnectionState, string> = {
    invalidated: 'O WhatsApp desconectou o computador',
    awaiting_app: 'Aguardando o app no computador',
    offline: 'Computador offline',
    pairing: 'Aguardando o QR code',
    reconnecting: 'Reconectando ao WhatsApp',
    connected: 'Conexão pelo computador',
};

type TestModeStatus = 'idle' | 'connecting' | 'qr';
type TestModeMethod = 'qr' | 'code';

export type AiBudgetState =
    | { status: 'loading' }
    | { status: 'loaded'; snapshot: AiBudgetApi.Snapshot; lastUpdatedAt: number }
    | { status: 'error' };

type AssistantSectionProps = {
    business: BusinessDetails | null;
    service: BusinessService | null;
    onReloadBusiness: () => Promise<void> | void;
    // Refreshes in the background, keeping what's on screen if it fails.
    onRefreshBusiness: () => Promise<void>;
    isLoadingBusiness: boolean;
    businessLoadError: string | null;
    isActive: boolean;
    isTrialEligible: boolean;
    modalSegments: string[];
    onNavigate: (segments: string[], replace?: boolean) => void;
    onNavigateToUpgrade: () => void;
    whatsappOnboardingConfigId: string;
    aiBudgetState: AiBudgetState;
    onLoadAiBudget: () => Promise<void> | void;
    onRefreshAiBudget: () => Promise<void> | void;
    // Set when the owner approves a connector computer, to follow it through pairing.
    connectorApprovedAt: number | null;
    // Stops following an approval: the computer was disconnected, or the owner went back without it.
    onConnectorApprovalSettled: () => void;
};

function useRemainingMinutes(expiresAt: number | null): number | null {
    const [remaining, setRemaining] = useState<number | null>(null);

    useEffect(() => {
        if (expiresAt === null) {
            setRemaining(null);
            return;
        }
        const tick = () => setRemaining(Math.max(0, Math.ceil((expiresAt - Date.now()) / 60_000)));
        tick();
        const interval = setInterval(tick, 30_000);
        return () => clearInterval(interval);
    }, [expiresAt]);

    return remaining;
}

function useTestMode(service: BusinessService | null, onReloadBusiness: () => Promise<void> | void) {
    const [status, setStatus] = useState<TestModeStatus>('idle');
    const [qrData, setQrData] = useState<string | null>(null);
    const [pairingCode, setPairingCode] = useState<string | null>(null);
    const wsRef = useRef<WebSocket | null>(null);

    const start = useCallback(() => {
        if (!service) return;
        if (wsRef.current) {
            wsRef.current.onclose = null;
            wsRef.current.close();
        }

        setStatus('connecting');
        setQrData(null);
        setPairingCode(null);

        const ws = service.createTestModeWebSocket();
        wsRef.current = ws;

        ws.onmessage = (event) => {
            try {
                const message = JSON.parse(event.data);
                if (message.type === 'qr') {
                    setQrData(message.data);
                    setStatus('qr');
                } else if (message.type === 'pairingCode') {
                    setPairingCode(message.code);
                } else if (message.type === 'connected') {
                    setStatus('idle');
                    onReloadBusiness();
                }
            } catch {
                /* ignore */
            }
        };

        ws.onclose = () => {
            if (wsRef.current !== ws) return;
            wsRef.current = null;
            setStatus('idle');
            Promise.resolve(onReloadBusiness()).catch(() => {});
        };
    }, [service, onReloadBusiness]);

    const requestPairingCode = useCallback((phoneNumber: string) => {
        if (!wsRef.current) return;
        wsRef.current.send(JSON.stringify({ type: 'requestPairingCode', phoneNumber }));
    }, []);

    const stop = useCallback(() => {
        if (wsRef.current) {
            wsRef.current.onclose = null;
            wsRef.current.close();
            wsRef.current = null;
        }
        setStatus('idle');
    }, []);

    useEffect(
        () => () => {
            wsRef.current?.close();
        },
        [],
    );

    return { status, qrData, pairingCode, start, stop, requestPairingCode };
}

export default function AssistantSection({
    business,
    service,
    onReloadBusiness,
    onRefreshBusiness,
    isLoadingBusiness,
    businessLoadError,
    isActive,
    isTrialEligible,
    modalSegments,
    onNavigate,
    onNavigateToUpgrade,
    whatsappOnboardingConfigId,
    aiBudgetState,
    onLoadAiBudget,
    onRefreshAiBudget,
    connectorApprovedAt,
    onConnectorApprovalSettled,
}: AssistantSectionProps) {
    const [isAuthorizing, setIsAuthorizing] = useState(false);
    const [isFinishing, setIsFinishing] = useState(false);
    const [testModeMethod, setTestModeMethod] = useState<TestModeMethod>(() => (isMobile() ? 'code' : 'qr'));
    const [isDisconnecting, setIsDisconnecting] = useState(false);
    const [botOverridesDraft, setBotOverridesDraft] = useState<BotOverride[]>([]);
    const [newOverridePhone, setNewOverridePhone] = useState('');
    const [assistantSaving, setAssistantSaving] = useState(false);
    const [botEnabled, setBotEnabled] = useState(false);
    const [connectPrelude, setConnectPrelude] = useState<ConnectPreludeStep | null>(null);
    const [showTestModeSuggestion, setShowTestModeSuggestion] = useState(false);
    const [connectionMethod, setConnectionMethod] = useState<ConnectionMethod>('cloud_api');
    const [showConnectorSetup, setShowConnectorSetup] = useState(false);
    const [isConnectorTermsChecked, setIsConnectorTermsChecked] = useState(false);
    const [isAcceptingConnectorTerms, setIsAcceptingConnectorTerms] = useState(false);
    const [testModeSuggestionShown, setTestModeSuggestionShown] = useState(false);
    const [isAutoMessagesHintDismissed, setIsAutoMessagesHintDismissed] = useState(false);
    const [confirmDialog, setConfirmDialog] = useState<{
        isOpen: boolean;
        title: string;
        message: string;
        confirmText: string;
        loadingText?: string;
        onConfirm: () => void;
    }>({ isOpen: false, title: '', message: '', confirmText: '', onConfirm: () => {} });
    const appliedBotDraftRef = useRef<BotDraft | null>(null);
    const toastedApprovalRef = useRef<number | null>(null);
    const wasConnectorSetupVisibleRef = useRef(false);

    const { showToast } = useToast();
    const testMode = useTestMode(service, onReloadBusiness);

    const {
        isOpen,
        open,
        close: closeModal,
    } = useSingleModalRoute({
        isActive,
        ready: true,
        segments: modalSegments,
        onNavigate,
        modalKey: 'config',
    });

    const close = useCallback(() => {
        if (testMode.status !== 'idle') testMode.stop();
        setShowConnectorSetup(false);
        closeModal();
    }, [closeModal, testMode]);

    const showConfigView = (business && hasPaidAccess(business)) ?? false;
    const canUseConnector = business !== null && hasConnectorAccess(business);
    const isTestMode = business?.whatsapp?.type === 'test_mode';
    const testModeExpiresAt = (business?.whatsapp?.type === 'test_mode' ? business.whatsapp.expiresAt : null) ?? null;
    const remainingMinutes = useRemainingMinutes(testModeExpiresAt);
    const connectorStatus = business?.whatsappConnector ?? null;
    const isConnectorApprovalPending = connectorApprovedAt !== null && connectorStatus === null;
    // A test-mode number is replaced once the computer links, so the setup takes over its screen.
    const canShowConnectorSetup = !business?.whatsapp || business.whatsapp.type === 'test_mode';
    const isConnectorSetupVisible =
        canShowConnectorSetup && (showConnectorSetup || connectorStatus !== null || isConnectorApprovalPending);
    const isConnectorTermsRequired = !business?.connectorTermsAccepted;
    const watchConnector =
        isConnectorApprovalPending || (isOpen && (isConnectorSetupVisible || business?.whatsapp?.type === 'connector'));

    useVisibleInterval(onRefreshBusiness, { enabled: watchConnector, intervalMs: 5_000, immediate: true });

    const stopTestMode = testMode.stop;
    useEffect(() => {
        if (connectorApprovedAt !== null) stopTestMode();
    }, [connectorApprovedAt, stopTestMode]);

    useEffect(() => {
        if (!isOpen || connectorApprovedAt === null || toastedApprovalRef.current === connectorApprovedAt) return;
        toastedApprovalRef.current = connectorApprovedAt;
        showToast('Computador autorizado! Volte para o OrganizaBot Conector.', 'success', 'modal');
    }, [isOpen, connectorApprovedAt, showToast]);

    const isConnectorLinked = business?.whatsapp?.type === 'connector';
    useEffect(() => {
        if (wasConnectorSetupVisibleRef.current && isConnectorLinked && isOpen) {
            showToast('WhatsApp conectado com sucesso!', 'success', 'modal');
        }
        wasConnectorSetupVisibleRef.current = isConnectorSetupVisible;
    }, [isConnectorSetupVisible, isConnectorLinked, isOpen, showToast]);

    // Drafts follow the server only until the owner edits them; polling must not wipe unsaved changes.
    useEffect(() => {
        if (!isOpen || !business) {
            appliedBotDraftRef.current = null;
            return;
        }
        const serverDraft = toBotDraft(business.bot);
        const appliedDraft = appliedBotDraftRef.current;
        if (appliedDraft) {
            if (isSameBotDraft(appliedDraft, serverDraft)) return;
            if (!isSameBotDraft(appliedDraft, { enabled: botEnabled, overrides: botOverridesDraft })) return;
        }
        appliedBotDraftRef.current = serverDraft;
        setBotEnabled(serverDraft.enabled);
        setBotOverridesDraft(serverDraft.overrides);
    }, [isOpen, business, botEnabled, botOverridesDraft]);

    if (!isActive) return null;

    if (businessLoadError) {
        return (
            <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                <div style={{ marginBottom: '12px', color: vars.foregroundColor }}>{businessLoadError}</div>
                <Button onClick={() => onReloadBusiness()} disabled={isLoadingBusiness}>
                    {isLoadingBusiness ? 'Carregando...' : 'Tentar novamente'}
                </Button>
            </div>
        );
    }

    if (!business) {
        return <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>Carregando assistente...</div>;
    }

    // Upsell view
    if (!showConfigView) {
        const handleUpgradeClick = () => {
            gtagEvent('assistant_upsell_click');
            onNavigateToUpgrade();
        };

        return (
            <UpsellContainer>
                <IconWrapper>
                    <WhatsAppIcon />
                </IconWrapper>

                <UpsellTitle>Assistente Virtual no WhatsApp</UpsellTitle>
                <UpsellSubtitle>
                    Automatize o atendimento do seu estabelecimento.
                    <br />
                    Seus clientes agendam direto pelo WhatsApp, 24 horas por dia.
                </UpsellSubtitle>

                <FeaturesGrid>
                    {features.map((feature, idx) => (
                        <FeatureCard key={idx}>
                            <FeatureIcon>{feature.icon}</FeatureIcon>
                            <FeatureTitle>{feature.title}</FeatureTitle>
                            <FeatureDescription>{feature.description}</FeatureDescription>
                        </FeatureCard>
                    ))}
                </FeaturesGrid>

                <CTACard>
                    {isTrialEligible && <Badge tone="highlight">7 dias grátis</Badge>}
                    <PriceNote>planos a partir de</PriceNote>
                    <PriceRow>
                        <PriceValue>{PlanCatalogue.formatPrice(PlanCatalogue.PAID.solo.priceInCents)}</PriceValue>
                        <PriceUnit>/mês</PriceUnit>
                    </PriceRow>
                    <PriceNote>
                        Cerca de {PlanCatalogue.PAID.solo.approximateConversationsPerMonth.toLocaleString('pt-BR')}{' '}
                        atendimentos por mês
                    </PriceNote>
                    <Button primary onClick={handleUpgradeClick} data-testid="assistant-upsell-upgrade">
                        {isTrialEligible ? 'Começar teste grátis' : 'Reativar'}
                    </Button>
                </CTACard>
            </UpsellContainer>
        );
    }

    // Config view handlers
    const handleAuthorize = async () => {
        if (!service) return;

        setConnectPrelude(null);
        gtagEvent('assistant_connect_click');
        setIsAuthorizing(true);
        testMode.stop();

        try {
            const metaEmbeddedSignup = new MetaEmbeddedSignup({ configId: whatsappOnboardingConfigId });
            const { code, session } = await metaEmbeddedSignup.login();
            const phoneNumberId = session?.phoneNumberId ?? null;

            setIsFinishing(true);

            await service.authorizeWhatsApp({
                code,
                session: phoneNumberId ? { phoneNumberId } : null,
            });

            await onReloadBusiness();

            showToast('WhatsApp conectado com sucesso!', 'success', 'modal');
        } catch (err) {
            if (err instanceof MetaEmbeddedSignupError) {
                service
                    .reportWhatsAppConnectionFailure({ metaEvent: err.metaEvent })
                    .catch((error) => console.error('Failed to report the WhatsApp connection failure', error));
                if (!testModeSuggestionShown) {
                    setTestModeSuggestionShown(true);
                    setShowTestModeSuggestion(true);
                }
                return;
            }
            console.error('Failed to authorize WhatsApp', err);
            showToast('Ocorreu um erro ao tentar conectar o WhatsApp. Tente novamente mais tarde.', 'error', 'modal');
        } finally {
            setIsAuthorizing(false);
            setIsFinishing(false);
        }
    };

    const handleConnectorDisconnectClick = () => {
        gtagEvent('connector_disconnect_click');
        setConfirmDialog({
            isOpen: true,
            title: 'Desconectar computador',
            // Nothing was linked yet while setting up: there's no WhatsApp to lose or assistant to stop.
            message: business?.whatsapp
                ? 'O OrganizaBot Conector deste computador será desconectado e o WhatsApp sairá dele. O assistente deixará de responder seus clientes até você conectar de novo.'
                : 'O OrganizaBot Conector deste computador será desconectado da sua conta.',
            confirmText: 'Desconectar',
            loadingText: 'Desconectando...',
            onConfirm: async () => {
                if (!service) return;
                setIsDisconnecting(true);
                try {
                    await service.disconnectWhatsAppConnector();
                    onConnectorApprovalSettled();
                    await onRefreshBusiness();
                    setConfirmDialog({ isOpen: false, title: '', message: '', confirmText: '', onConfirm: () => {} });
                    setShowConnectorSetup(false);
                    showToast('Computador desconectado.', 'success', 'modal');
                } catch (error) {
                    console.error('Failed to disconnect the WhatsApp connector', error);
                    showToast('Não foi possível desconectar o computador.', 'error', 'modal');
                    setConfirmDialog({ isOpen: false, title: '', message: '', confirmText: '', onConfirm: () => {} });
                } finally {
                    setIsDisconnecting(false);
                }
            },
        });
    };

    const handleContinueConnecting = async () => {
        gtagEvent('assistant_connection_method_continue', { type: connectionMethod });
        if (connectionMethod !== 'connector') {
            setConnectPrelude('business-app-question');
            return;
        }
        if (isConnectorTermsRequired) {
            if (!service) return;
            setIsAcceptingConnectorTerms(true);
            try {
                await service.acceptWhatsAppConnectorTerms();
            } catch (error) {
                console.error('Failed to accept the WhatsApp connector terms', error);
                showToast('Não foi possível continuar. Tente novamente.', 'error', 'modal');
                return;
            } finally {
                setIsAcceptingConnectorTerms(false);
            }
            onRefreshBusiness();
        }
        testMode.stop();
        setShowConnectorSetup(true);
    };

    const handleDisconnectClick = () => {
        gtagEvent('assistant_disconnect_click');
        const viaConnector = business?.whatsapp?.type === 'connector';
        setConfirmDialog({
            isOpen: true,
            title: 'Desconectar WhatsApp',
            message: viaConnector
                ? 'Tem certeza que deseja desconectar o WhatsApp? O computador com o OrganizaBot Conector também será desconectado e o assistente virtual deixará de funcionar para seus clientes.'
                : 'Tem certeza que deseja desconectar o WhatsApp? O assistente virtual deixará de funcionar para seus clientes.',
            confirmText: 'Desconectar',
            loadingText: 'Desconectando...',
            onConfirm: async () => {
                if (!service) return;
                setIsDisconnecting(true);
                try {
                    if (viaConnector) {
                        await service.disconnectWhatsAppConnector();
                        onConnectorApprovalSettled();
                        await onRefreshBusiness();
                    } else {
                        await service.disconnectWhatsApp({ testMode: false });
                        await onReloadBusiness();
                    }
                    setConfirmDialog({ isOpen: false, title: '', message: '', confirmText: '', onConfirm: () => {} });
                    close();
                    showToast('WhatsApp desconectado.', 'success', 'app');
                } catch (error) {
                    console.error('Failed to disconnect WhatsApp', error);
                    showToast('Não foi possível desconectar o WhatsApp.', 'error', 'modal');
                    setConfirmDialog({ isOpen: false, title: '', message: '', confirmText: '', onConfirm: () => {} });
                } finally {
                    setIsDisconnecting(false);
                }
            },
        });
    };

    const handleTestModeDisconnect = async () => {
        if (!service) return;
        gtagEvent('assistant_test_mode_disconnect_click');
        setIsDisconnecting(true);
        try {
            testMode.stop();
            await service.disconnectWhatsApp({ testMode: true });
            await onReloadBusiness();
            showToast('Modo de teste desconectado.', 'success', 'modal');
        } catch (error) {
            console.error('Failed to disconnect test mode', error);
            showToast('Não foi possível desconectar o modo de teste.', 'error', 'modal');
        } finally {
            setIsDisconnecting(false);
        }
    };

    const handleAutoMessagesHintDismiss = async () => {
        if (!service) return;
        gtagEvent('assistant_auto_messages_hint_dismiss');
        setIsAutoMessagesHintDismissed(true);
        try {
            await service.dismissAutoMessagesHint();
        } catch (error) {
            console.error('Failed to dismiss the auto messages hint', error);
        }
    };

    const handleBotOverrideChange = (phoneNumber: string, enabled: boolean) => {
        setBotOverridesDraft((prev) => prev.map((o) => (o.phoneNumber === phoneNumber ? { ...o, enabled } : o)));
    };

    const handleRemoveOverride = (phoneNumber: string) => {
        setBotOverridesDraft((prev) => prev.filter((o) => o.phoneNumber !== phoneNumber));
    };

    const handleAddOverride = () => {
        gtagEvent('assistant_add_override_click');
        const phone = newOverridePhone.trim();
        if (!phone) return;

        const phoneDigits = onlyDigits(phone);
        if (phoneDigits.length < 10) {
            showToast('Telefone inválido. Deve conter pelo menos 10 dígitos.', 'error', 'modal');
            return;
        }

        const normalizedPhone =
            phoneDigits.length === 11 && phoneDigits.startsWith('55') ? phoneDigits.slice(2) : phoneDigits;

        setBotOverridesDraft((prev) => {
            if (prev.some((o) => o.phoneNumber === normalizedPhone)) {
                showToast('Este telefone já está na lista de exceções.', 'error', 'modal');
                return prev;
            }
            return [...prev, { phoneNumber: normalizedPhone, enabled: botEnabled }];
        });
        setNewOverridePhone('');
    };

    const handleSaveAssistant = async () => {
        if (!service || !business?.whatsapp) return;
        gtagEvent('assistant_save_click', {
            action: botEnabled ? 'active' : 'inactive',
            type: business.whatsapp.type,
        });
        setAssistantSaving(true);
        try {
            await service.setBot({
                enabled: botEnabled,
                overrides: botOverridesDraft.map((override) => ({
                    ...override,
                    phoneNumber: '55' + onlyDigits(override.phoneNumber),
                })),
            });
            await onReloadBusiness();
            showToast('Configurações salvas com sucesso!', 'success', 'app');
            close();
        } catch (error) {
            console.error('Failed to save assistant settings', error);
            showToast('Não foi possível salvar as configurações.', 'error', 'modal');
        } finally {
            setAssistantSaving(false);
        }
    };

    const getSummary = () => {
        if (!business?.whatsapp) return ['WhatsApp não conectado'];
        const items = [
            formatPhone(stripCountryCode(business.whatsapp.phoneNumber)),
            business.bot.enabled ? 'Assistente ativo' : 'Assistente inativo',
        ];
        if (isTestMode) {
            items.push('Modo de teste');
        }
        if (business.whatsapp.type === 'connector') {
            const state = connectorConnectionState({
                invalidated: business.whatsapp.invalidated,
                status: connectorStatus,
            });
            items.push(connectorSummaryLabels[state]);
        }
        return items;
    };

    // Config view
    return (
        <ConfigContainer>
            <Header>
                <SectionTitle>Assistente</SectionTitle>
            </Header>

            <ConfigCard
                data-testid="whatsapp-assistant-card"
                onClick={() => {
                    gtagEvent('assistant_config_click');
                    open();
                }}
            >
                <ConfigInfo>
                    <ConfigTitle>
                        Assistente no WhatsApp
                        {isTestMode && (
                            <>
                                {' '}
                                <Badge tone="warning">Modo de teste</Badge>
                            </>
                        )}
                    </ConfigTitle>
                    <ConfigSummary>
                        {getSummary().map((item, idx) => (
                            <SummaryItem key={idx}>{item}</SummaryItem>
                        ))}
                    </ConfigSummary>
                </ConfigInfo>
            </ConfigCard>

            {business && (
                <PersonalizationCard
                    business={business}
                    service={service}
                    onReloadBusiness={onReloadBusiness}
                    isActive={isActive}
                    modalSegments={modalSegments}
                    onNavigate={onNavigate}
                />
            )}

            {business && (
                <OpenScopeCard
                    business={business}
                    service={service}
                    onReloadBusiness={onReloadBusiness}
                    isActive={isActive}
                    modalSegments={modalSegments}
                    onNavigate={onNavigate}
                />
            )}

            {business && (
                <TemplateMappingsCard
                    business={business}
                    service={service}
                    isActive={isActive}
                    modalSegments={modalSegments}
                    onNavigate={onNavigate}
                />
            )}

            <CreditBudgetCard
                service={service}
                business={business}
                state={aiBudgetState}
                onLoad={onLoadAiBudget}
                onRefresh={onRefreshAiBudget}
                onReloadBusiness={onReloadBusiness}
            />

            <Modal
                isOpen={isOpen}
                onClose={close}
                data-testid="assistant-modal"
                title="Assistente no WhatsApp"
                footer={
                    testMode.status !== 'idle' ? (
                        <Button
                            onClick={() => {
                                gtagEvent('assistant_cancel_click');
                                testMode.stop();
                            }}
                        >
                            Cancelar
                        </Button>
                    ) : isConnectorSetupVisible ? (
                        <>
                            {connectorStatus === null && (
                                <Button
                                    onClick={() => {
                                        onConnectorApprovalSettled();
                                        setShowConnectorSetup(false);
                                    }}
                                >
                                    Voltar
                                </Button>
                            )}
                            <Button primary onClick={close}>
                                Fechar
                            </Button>
                        </>
                    ) : business?.whatsapp ? (
                        <>
                            <Button
                                onClick={() => {
                                    gtagEvent('assistant_cancel_click');
                                    close();
                                }}
                                disabled={assistantSaving}
                            >
                                Cancelar
                            </Button>
                            <Button primary onClick={handleSaveAssistant} disabled={assistantSaving}>
                                {assistantSaving ? 'Salvando...' : 'Salvar'}
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button
                                onClick={() => {
                                    gtagEvent('assistant_cancel_click');
                                    close();
                                }}
                                disabled={isAuthorizing || isAcceptingConnectorTerms}
                            >
                                Cancelar
                            </Button>
                            <Button
                                primary
                                onClick={handleContinueConnecting}
                                disabled={
                                    isAuthorizing ||
                                    isAcceptingConnectorTerms ||
                                    (connectionMethod === 'connector' &&
                                        isConnectorTermsRequired &&
                                        !isConnectorTermsChecked)
                                }
                                data-testid="assistant-connect-continue"
                            >
                                {isFinishing
                                    ? 'Finalizando...'
                                    : isAuthorizing
                                      ? 'Conectando...'
                                      : isAcceptingConnectorTerms
                                        ? 'Continuando...'
                                        : canUseConnector
                                          ? 'Continuar'
                                          : 'Conectar WhatsApp'}
                            </Button>
                        </>
                    )
                }
            >
                {testMode.status !== 'idle' ? (
                    <FormGroup style={{ gap: '20px' }}>
                        <TestModeWarning>
                            Modo de teste — conexão temporária e não oficial, sujeita a bloqueio pelo WhatsApp. Para uso
                            permanente, conecte via API oficial.
                        </TestModeWarning>

                        {testModeMethod === 'qr' ? (
                            <>
                                <TestModeQRContainer>
                                    {testMode.status === 'connecting' && (
                                        <TestModeStatusText>Conectando...</TestModeStatusText>
                                    )}
                                    {testMode.status === 'qr' && (
                                        <>
                                            <QRCodeSVG value={testMode.qrData!} size={256} />
                                            <TestModeStatusText>Escaneie o QR code com o WhatsApp</TestModeStatusText>
                                        </>
                                    )}
                                </TestModeQRContainer>
                                <TestModeLink onClick={() => setTestModeMethod('code')}>
                                    Conectar pelo código
                                </TestModeLink>
                            </>
                        ) : (
                            <>
                                <TestModePairingSection
                                    pairingCode={testMode.pairingCode}
                                    onRequestCode={(phoneNumber) => testMode.requestPairingCode(phoneNumber)}
                                />
                                <TestModeLink onClick={() => setTestModeMethod('qr')}>
                                    Conectar pelo QR code
                                </TestModeLink>
                            </>
                        )}
                    </FormGroup>
                ) : isConnectorSetupVisible ? (
                    <FormGroup style={{ gap: '20px' }}>
                        <ConnectorSetup
                            status={connectorStatus}
                            isApprovalPending={isConnectorApprovalPending}
                            onDisconnect={handleConnectorDisconnectClick}
                            isDisconnecting={isDisconnecting}
                        />
                    </FormGroup>
                ) : business?.whatsapp ? (
                    <FormGroup style={{ gap: '20px' }}>
                        <InputGroup>
                            Número conectado
                            <Input
                                value={formatPhone(stripCountryCode(business.whatsapp.phoneNumber))}
                                readOnly
                                disabled
                            />
                        </InputGroup>

                        {business.whatsapp.type === 'connector' && (
                            <ConnectorConnectionStatus
                                invalidated={business.whatsapp.invalidated}
                                status={connectorStatus}
                            />
                        )}

                        {business.whatsapp.type === 'cloud_api' && business.whatsapp.invalidated && (
                            <TestModeWarning>
                                <strong>Conexão com o WhatsApp expirou.</strong>
                                <p style={{ margin: '8px 0 12px' }}>
                                    A autorização desse número junto à Meta foi removida ou expirou. Reconecte para
                                    voltar a usar o assistente.
                                </p>
                                <Button
                                    primary
                                    onClick={() => setConnectPrelude('meta-window')}
                                    disabled={isAuthorizing}
                                >
                                    {isAuthorizing ? 'Reconectando...' : 'Reconectar agora'}
                                </Button>
                            </TestModeWarning>
                        )}

                        {isTestMode && (
                            <TestModeWarning>
                                <TestModeConnectedRow>
                                    <PulseDot />
                                    <strong>
                                        Conexão ativa em modo de teste
                                        {remainingMinutes !== null ? ` (${remainingMinutes} min restantes)` : ''}
                                    </strong>
                                </TestModeConnectedRow>
                                <p style={{ margin: '8px 0 0' }}>
                                    Conexão temporária e não oficial, sujeita a bloqueio pelo WhatsApp. Para uso
                                    permanente, conecte via API oficial da Meta.
                                </p>
                            </TestModeWarning>
                        )}

                        {/* The app's auto-replies arrive as the owner's own messages, which hold the assistant back for 5 minutes. */}
                        {!business.isAutoMessagesHintDismissed && !isAutoMessagesHintDismissed && (
                            <AutoMessagesHint $severity="info" data-testid="auto-messages-hint">
                                <span>
                                    <strong>
                                        Desative a <em>Mensagem de saudação</em> e a <em>Mensagem de ausência</em>
                                    </strong>{' '}
                                    no app WhatsApp Business (Configurações → Ferramentas comerciais). Com elas ativas,
                                    o assistente demora cerca de 5 minutos para responder cada cliente novo.
                                </span>
                                <Button
                                    onClick={handleAutoMessagesHintDismiss}
                                    data-testid="auto-messages-hint-dismiss"
                                >
                                    Já desativei
                                </Button>
                            </AutoMessagesHint>
                        )}

                        <div
                            style={{
                                borderTop: `1px solid ${vars.dividerColor}`,
                                paddingTop: '20px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '20px',
                            }}
                        >
                            <InputGroup>
                                <CheckboxLabel>
                                    <Checkbox checked={botEnabled} onChange={(e) => setBotEnabled(e.target.checked)} />
                                    Assistente Virtual ativo
                                </CheckboxLabel>
                            </InputGroup>
                            <InputGroup>
                                <LabelRow>Exceções por cliente</LabelRow>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                        <Input
                                            type="tel"
                                            placeholder="(00) 00000-0000"
                                            value={newOverridePhone}
                                            onChange={(e) => setNewOverridePhone(formatPhone(e.target.value))}
                                            maxLength={15}
                                            style={{ flex: '1 1 200px', minWidth: '200px' }}
                                        />
                                        <Button onClick={handleAddOverride}>Adicionar</Button>
                                    </div>
                                    <div style={{ display: 'grid', gap: '8px' }}>
                                        {botOverridesDraft.map((override) => (
                                            <div
                                                key={override.phoneNumber}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    gap: '12px',
                                                }}
                                            >
                                                <span style={{ fontWeight: 600 }}>
                                                    {formatPhone(stripCountryCode(override.phoneNumber))}
                                                </span>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <CheckboxLabel>
                                                        <Checkbox
                                                            checked={override.enabled}
                                                            onChange={(e) =>
                                                                handleBotOverrideChange(
                                                                    override.phoneNumber,
                                                                    e.target.checked,
                                                                )
                                                            }
                                                        />
                                                        Assistente ativo
                                                    </CheckboxLabel>
                                                    <Button onClick={() => handleRemoveOverride(override.phoneNumber)}>
                                                        Remover
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </InputGroup>
                        </div>

                        {business.whatsapp.type === 'test_mode' ? (
                            <div style={{ borderTop: `1px solid ${vars.dividerColor}`, paddingTop: '20px' }}>
                                <Button primary onClick={handleTestModeDisconnect}>
                                    Desconectar teste
                                </Button>
                            </div>
                        ) : business.whatsapp.type === 'connector' ? (
                            <div
                                style={{
                                    borderTop: `1px solid ${vars.dividerColor}`,
                                    paddingTop: '20px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '16px',
                                }}
                            >
                                <ConnectorRiskNotice>
                                    Conexão não oficial com o WhatsApp: existe risco de o WhatsApp restringir ou banir o
                                    número. Para mais segurança, use a conexão oficial.
                                </ConnectorRiskNotice>
                                <Button
                                    onClick={handleDisconnectClick}
                                    disabled={isDisconnecting}
                                    style={{ width: 'fit-content' }}
                                >
                                    Desconectar WhatsApp
                                </Button>
                            </div>
                        ) : (
                            <div
                                style={{
                                    borderTop: `1px solid ${vars.dividerColor}`,
                                    paddingTop: '20px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '16px',
                                }}
                            >
                                <MetaBillingNotice data-testid="meta-billing-notice">
                                    <span>
                                        <strong>Cadastre um cartão de crédito na Meta.</strong> Sem ele, lembretes e
                                        confirmações não são enviados e as respostas do assistente param de chegar
                                        quando acabam as mensagens gratuitas do mês. A Meta cobra ~R$&nbsp;0,04 por
                                        mensagem.
                                    </span>
                                    <Button
                                        as="a"
                                        primary
                                        href={business.whatsapp.billingUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        onClick={() => gtagEvent('assistant_billing_meta_click')}
                                    >
                                        Configurar cartão na Meta
                                    </Button>
                                </MetaBillingNotice>
                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                    <Button
                                        as="a"
                                        href={business.whatsapp.managerUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        onClick={() => gtagEvent('assistant_manage_meta_click')}
                                    >
                                        Gerenciar na Meta
                                    </Button>
                                    <Button onClick={handleDisconnectClick} disabled={isDisconnecting}>
                                        Desconectar WhatsApp
                                    </Button>
                                </div>
                            </div>
                        )}
                    </FormGroup>
                ) : (
                    <FormGroup>
                        <p style={{ margin: 0 }}>
                            Conecte o <strong>número do estabelecimento</strong> para ativar o assistente virtual no
                            WhatsApp.
                        </p>
                        <p style={{ margin: 0 }}>
                            Com o assistente ativo, seus clientes podem agendar, consultar e cancelar horários
                            diretamente pelo WhatsApp, 24 horas por dia.
                        </p>
                        {canUseConnector ? (
                            <ConnectionChoice selected={connectionMethod} onSelect={setConnectionMethod} />
                        ) : (
                            <p
                                style={{ margin: 0, opacity: 0.7, fontSize: '14px' }}
                                data-testid="official-connection-note"
                            >
                                Trabalhamos com a API oficial da Meta, proprietária do WhatsApp. Essa autorização
                                legitima o uso do seu número e mantém a operação dentro das regras da plataforma.
                            </p>
                        )}
                        {connectionMethod === 'connector' && isConnectorTermsRequired && (
                            <ConnectorTermsCheckbox
                                checked={isConnectorTermsChecked}
                                onChange={setIsConnectorTermsChecked}
                            />
                        )}
                        {isFinishing && (
                            <div
                                style={{
                                    padding: '8px 12px',
                                    border: `1px solid ${vars.dividerColor}`,
                                    backgroundColor: vars.primaryColorLight,
                                    borderRadius: '6px',
                                    color: vars.foregroundColor,
                                }}
                            >
                                Estamos finalizando a autorização com a Meta. Isso pode levar alguns instantes...
                            </div>
                        )}

                        {!isAuthorizing && (
                            <TestModeLink
                                onClick={() => {
                                    gtagEvent('assistant_test_mode_link_click');
                                    testMode.start();
                                }}
                                data-testid="test-mode-link"
                            >
                                Quer testar antes? Use o modo de teste
                            </TestModeLink>
                        )}
                    </FormGroup>
                )}
            </Modal>

            <ConfirmDialog
                isOpen={confirmDialog.isOpen}
                title={confirmDialog.title}
                message={confirmDialog.message}
                confirmText={confirmDialog.confirmText}
                onConfirm={confirmDialog.onConfirm}
                onCancel={() =>
                    setConfirmDialog({ isOpen: false, title: '', message: '', confirmText: '', onConfirm: () => {} })
                }
                isLoading={isDisconnecting}
                loadingText={confirmDialog.loadingText}
            />

            <Dialog
                isOpen={connectPrelude !== null}
                onClose={() => setConnectPrelude(null)}
                data-testid="connect-prelude"
            >
                <DialogHeader>
                    <DialogTitle>Conectar WhatsApp</DialogTitle>
                </DialogHeader>
                {connectPrelude === 'business-app-question' && (
                    <>
                        <DialogContent>
                            <PreludeBody data-testid="connect-prelude-business-app-question">
                                <strong>Seu número está no app WhatsApp Business?</strong>
                                <span style={{ opacity: 0.8 }}>
                                    Para o assistente atender no seu número de sempre, a Meta pede que ele esteja no app
                                    WhatsApp Business, não no WhatsApp comum.
                                </span>
                            </PreludeBody>
                        </DialogContent>
                        <DialogFooter style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                            <Button
                                primary
                                onClick={() => {
                                    gtagEvent('assistant_connect_business_app_answer', { type: 'business' });
                                    setConnectPrelude('meta-window');
                                }}
                            >
                                Sim, uso o WhatsApp Business
                            </Button>
                            <Button
                                onClick={() => {
                                    gtagEvent('assistant_connect_business_app_answer', { type: 'regular' });
                                    setConnectPrelude('move-to-business-app');
                                }}
                            >
                                Não, uso o WhatsApp comum
                            </Button>
                        </DialogFooter>
                    </>
                )}
                {connectPrelude === 'move-to-business-app' && (
                    <>
                        <DialogContent>
                            <PreludeBody data-testid="connect-prelude-move-to-business-app">
                                <strong>Passe seu número para o WhatsApp Business</strong>
                                <span style={{ opacity: 0.8 }}>
                                    É grátis, usa o mesmo número e traz suas conversas junto. Seus clientes não percebem
                                    nada.
                                </span>
                                <PreludeSteps>
                                    <li>
                                        Instale o WhatsApp Business pela{' '}
                                        <a
                                            href="https://play.google.com/store/apps/details?id=com.whatsapp.w4b"
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            Play Store
                                        </a>{' '}
                                        ou pela{' '}
                                        <a
                                            href="https://apps.apple.com/app/whatsapp-business/id1386412706"
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            App Store
                                        </a>
                                        .
                                    </li>
                                    <li>Abra o app e confirme o seu número de sempre.</li>
                                    <li>Volte aqui e continue.</li>
                                </PreludeSteps>
                            </PreludeBody>
                        </DialogContent>
                        <DialogFooter>
                            <Button onClick={() => setConnectPrelude(null)}>Cancelar</Button>
                            <Button
                                primary
                                onClick={() => {
                                    gtagEvent('assistant_connect_moved_to_business_app_click');
                                    setConnectPrelude('meta-window');
                                }}
                            >
                                Já mudei, continuar
                            </Button>
                        </DialogFooter>
                    </>
                )}
                {connectPrelude === 'meta-window' && (
                    <>
                        <DialogContent>
                            <PreludeBody data-testid="connect-prelude-meta-window" style={{ alignItems: 'center' }}>
                                <img
                                    src="/meta-integration-illustration.png"
                                    alt=""
                                    style={{ width: '100%', maxWidth: '300px' }}
                                />
                                <span style={{ opacity: 0.8 }}>
                                    A conexão é feita pela Meta (Facebook), dona do WhatsApp. Se parecer complicado,
                                    fique tranquilo — é o caminho oficial e você pode desfazer quando quiser.
                                </span>
                                <span style={{ opacity: 0.8 }}>
                                    Na janela da Meta, escolha conectar o <strong>app WhatsApp Business</strong> que
                                    você já usa. Se aparecer só a opção de cadastrar o número por SMS ou ligação, pare e{' '}
                                    <a
                                        href="https://wa.me/5547984627950?text=Olá,%20preciso%20de%20ajuda%20para%20conectar%20o%20WhatsApp%20no%20OrganizaBot."
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        fale com o suporte
                                    </a>
                                    .
                                </span>
                            </PreludeBody>
                        </DialogContent>
                        <DialogFooter>
                            <Button onClick={() => setConnectPrelude(null)}>Cancelar</Button>
                            <Button primary onClick={handleAuthorize}>
                                Continuar
                            </Button>
                        </DialogFooter>
                    </>
                )}
            </Dialog>

            <Dialog isOpen={showTestModeSuggestion} onClose={() => setShowTestModeSuggestion(false)}>
                <DialogHeader>
                    <DialogTitle>Não conseguiu conectar?</DialogTitle>
                </DialogHeader>
                <DialogContent>
                    <span style={{ opacity: 0.8 }}>
                        {canUseConnector
                            ? 'Alguns números não passam na verificação da Meta. Você pode conectar pelo computador com o OrganizaBot Conector (conexão não oficial, com risco para o número) ou experimentar o modo de teste — você testa o assistente em 30 segundos, sem burocracia.'
                            : 'Experimente o modo de teste — você testa o assistente em 30 segundos, sem burocracia.'}
                    </span>
                </DialogContent>
                {canUseConnector ? (
                    <DialogFooter style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                        <Button
                            primary
                            onClick={() => {
                                gtagEvent('assistant_connector_suggestion_click');
                                setShowTestModeSuggestion(false);
                                setConnectionMethod('connector');
                                if (!isConnectorTermsRequired) setShowConnectorSetup(true);
                            }}
                        >
                            Conectar pelo computador
                        </Button>
                        <Button
                            onClick={() => {
                                setShowTestModeSuggestion(false);
                                testMode.start();
                            }}
                        >
                            Testar agora no modo de teste
                        </Button>
                    </DialogFooter>
                ) : (
                    <DialogFooter>
                        <Button onClick={() => setShowTestModeSuggestion(false)}>Fechar</Button>
                        <Button
                            primary
                            onClick={() => {
                                setShowTestModeSuggestion(false);
                                testMode.start();
                            }}
                        >
                            Testar agora
                        </Button>
                    </DialogFooter>
                )}
            </Dialog>
        </ConfigContainer>
    );
}

function toBotDraft(bot: BusinessDetails['bot']): BotDraft {
    return {
        enabled: bot.enabled,
        overrides: (bot.overrides ?? []).map((override) => ({
            ...override,
            phoneNumber: stripCountryCode(override.phoneNumber),
        })),
    };
}

function isSameBotDraft(a: BotDraft, b: BotDraft): boolean {
    return JSON.stringify(a) === JSON.stringify(b);
}
